import { useCallback, useEffect, useRef } from 'react';
import { useAccountChanges, type AccountChangeEvent } from '@/modules/mcpStream';

interface Args {
  storyId: string | null | undefined;
  versionIds: ReadonlySet<string>;
  imageIds: ReadonlySet<string>;
  refresh: () => void;
  refreshTemplateStatus: () => void;
}

// A new version or image elsewhere names no story, so a created one is read in case it is this story's
function aboutThisStory(event: AccountChangeEvent, { storyId, versionIds, imageIds }: Args) {
  const id = event.entityUniqueId;
  switch (event.entity) {
    case 'story':
    case 'shareLink':
      return id === storyId;
    case 'scene':
      return event.op === 'created' || (!!id && versionIds.has(id));
    case 'image':
      return event.op === 'created' || (!!id && imageIds.has(id));
    default:
      return false;
  }
}

export function useStoryChanges(args: Args) {
  const owed = useRef({ story: false, template: false });
  const latest = useRef(args);
  useEffect(() => {
    latest.current = args;
  });

  const run = useCallback(() => {
    const { story, template } = owed.current;
    owed.current = { story: false, template: false };
    if (story) latest.current.refresh();
    if (template) latest.current.refreshTemplateStatus();
  }, []);

  useAccountChanges(['story', 'scene', 'image', 'shareLink', 'templateSubmission'], (events) => {
    // An empty batch means the stream reopened and changes may have been missed
    const missed = events.length === 0;
    if (missed || events.some((event) => aboutThisStory(event, latest.current))) owed.current.story = true;
    if (missed || events.some((event) => event.entity === 'templateSubmission')) owed.current.template = true;
    if (!document.hidden) run();
  }, 1000);

  useEffect(() => {
    const onVisibility = () => {
      if (!document.hidden) run();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [run]);
}
